'use client'

import { useState, useMemo } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { SongArtwork } from '@/components/ui/song-artwork'
import {
  InlineDialog,
  InlineDialogHeader,
  InlineDialogFooter,
  InlineDialogTitle,
  InlineDialogDescription,
} from './inline-dialog'
import {
  Music,
  Play,
  Search,
  ChevronLeft,
  ChevronRight,
  VolumeX,
  Volume2,
  AlertTriangle,
} from 'lucide-react'
import { useTranslations } from 'next-intl'
import type { DemoSong, DemoQueueRequest, DemoPlayedSong, DemoNowPlaying, DemoSquelchedSong } from '../demo-types'

const REPERTOIRE_PAGE_SIZE = 12

export interface RepertoireSearchProps {
  songs: DemoSong[]
  queue: DemoQueueRequest[]
  playedSongs: DemoPlayedSong[]
  nowPlaying: DemoNowPlaying | null
  squelchedSongs: DemoSquelchedSong[]
  onPlaySong: (song: DemoSong) => void
  onSquelch: (songId: string, durationMinutes: number) => void
  onUnsquelch: (songId: string) => void
}

export function RepertoireSearch({
  songs,
  queue,
  playedSongs,
  nowPlaying,
  squelchedSongs,
  onPlaySong,
  onSquelch,
  onUnsquelch,
}: RepertoireSearchProps) {
  const t = useTranslations('demo')
  const [searchQuery, setSearchQuery] = useState('')
  const [repertoirePage, setRepertoirePage] = useState(0)
  const [confirmReplaceSong, setConfirmReplaceSong] = useState<{ isOpen: boolean; pendingSong: DemoSong | null }>({ isOpen: false, pendingSong: null })

  // Filter songs for repertoire
  const filteredSongs = useMemo(() => {
    if (!searchQuery.trim()) return songs
    const query = searchQuery.toLowerCase()
    return songs.filter(
      s => s.title.toLowerCase().includes(query) || s.artist.toLowerCase().includes(query)
    )
  }, [songs, searchQuery])

  // Paginated repertoire
  const paginatedRepertoire = filteredSongs.slice(
    repertoirePage * REPERTOIRE_PAGE_SIZE,
    (repertoirePage + 1) * REPERTOIRE_PAGE_SIZE
  )
  const totalRepertoirePages = Math.ceil(filteredSongs.length / REPERTOIRE_PAGE_SIZE)

  // Utility: check if song is squelched
  const isSongSquelched = (songId: string) =>
    squelchedSongs.some(s => s.songId === songId && s.until.getTime() > Date.now())

  // Utility: check if song is in queue
  const isSongInQueue = (songId: string) =>
    queue.some(r => r.songId === songId)

  const handlePlayFromRepertoire = (song: DemoSong) => {
    if (nowPlaying) {
      setConfirmReplaceSong({ isOpen: true, pendingSong: song })
    } else {
      onPlaySong(song)
    }
  }

  const handleConfirmReplace = () => {
    if (!confirmReplaceSong.pendingSong) return
    onPlaySong(confirmReplaceSong.pendingSong)
    setConfirmReplaceSong({ isOpen: false, pendingSong: null })
  }

  return (
    <>
      <Card className="border-zinc-800 bg-zinc-900/50">
        <CardHeader className="pb-2 px-4">
          <div className="flex items-center justify-between gap-2 sm:gap-4">
            <CardTitle className="text-white text-base shrink-0">{t('yourRepertoire')}</CardTitle>
            <div className="flex items-center gap-2 flex-1 justify-end">
              <div className="relative flex-1 max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
                <Input
                  placeholder={t('searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setRepertoirePage(0) }}
                  className="pl-9 bg-zinc-800 border-zinc-700 text-white h-8 text-sm"
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          {paginatedRepertoire.length > 0 ? (
            <>
              <div className="space-y-1.5">
                {paginatedRepertoire.map((song, songIndex) => {
                  const inQueue = isSongInQueue(song.id)
                  const squelched = isSongSquelched(song.id)
                  const alreadyPlayed = playedSongs.some(p => p.song.id === song.id)
                  const queueRequest = queue.find(r => r.songId === song.id)
                  const hasTips = queueRequest && queueRequest.totalTipsCents > 0
                  const hasUpvotes = queueRequest && queueRequest.upvotes > 0
                  const isRequested = inQueue || hasUpvotes || hasTips

                  const borderClass = squelched
                    ? 'bg-red-500/5 border border-red-500/20 hover:border-red-500/40 opacity-60'
                    : hasTips
                    ? 'bg-gradient-to-r from-emerald-500/15 to-green-500/10 border border-emerald-500/40 hover:border-emerald-500/60'
                    : isRequested
                    ? 'bg-violet-500/10 border border-violet-500/30 hover:border-violet-500/50'
                    : alreadyPlayed
                    ? 'bg-zinc-800/30 border border-zinc-700/30 hover:bg-zinc-800/50'
                    : 'bg-zinc-800/50 border border-transparent hover:bg-zinc-800/70 hover:border-zinc-700/50'

                  return (
                    <div key={song.id} {...(songIndex === 2 ? { 'data-coach': 'pick-song' } : {})} className={`flex items-center gap-2 p-2 rounded-lg group transition-colors ${borderClass}`}>
                      <div className="relative">
                        <SongArtwork src={song.artworkUrl} size={40} className={`h-10 w-10 rounded shrink-0 ${(alreadyPlayed && !isRequested) || squelched ? 'grayscale opacity-70' : ''}`} />
                        {squelched && (
                          <div className="absolute -top-1 -left-1 bg-red-500 text-white text-[7px] font-bold px-1 rounded flex items-center gap-0.5">
                            <VolumeX className="h-2 w-2" />
                          </div>
                        )}
                        {hasTips && (
                          <div className="absolute -top-1 -right-1 bg-emerald-500 text-white text-[8px] font-bold px-1 rounded">
                            ${(queueRequest.totalTipsCents / 100).toFixed(0)}
                          </div>
                        )}
                        {!hasTips && hasUpvotes && (
                          <div className="absolute -top-1 -right-1 bg-violet-500 text-white text-[8px] font-bold px-1 rounded">
                            ▲{queueRequest.upvotes}
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium truncate ${(alreadyPlayed && !isRequested) || squelched ? 'text-zinc-500' : 'text-white'}`}>{song.title}</p>
                        <div className={`flex items-center gap-1.5 text-xs truncate ${(alreadyPlayed && !isRequested) || squelched ? 'text-zinc-600' : 'text-zinc-400'}`}>
                          <span className="truncate">{song.artist}</span>
                          {squelched && <span className="text-red-400 shrink-0">• {t('hidden')}</span>}
                          {!squelched && inQueue && <span className="text-violet-400 shrink-0">• {t('inQueue')}</span>}
                          {!squelched && alreadyPlayed && !isRequested && <span className="text-zinc-500 shrink-0">• {t('played')}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {squelched ? (
                          <Button size="sm" variant="ghost" onClick={() => onUnsquelch(song.id)} className="h-7 w-7 p-0 text-red-400 hover:text-green-400 hover:bg-green-500/10">
                            <Volume2 className="h-3 w-3" />
                          </Button>
                        ) : (
                          <Button size="sm" variant="ghost" onClick={() => onSquelch(song.id, 30)} className="h-7 w-7 p-0 text-zinc-500 hover:text-red-400 hover:bg-red-500/10">
                            <VolumeX className="h-3 w-3" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          onClick={() => handlePlayFromRepertoire(song)}
                          className={`h-9 w-9 p-0 ${
                            hasTips
                              ? 'bg-emerald-600 hover:bg-emerald-700'
                              : isRequested
                              ? 'bg-violet-600 hover:bg-violet-700'
                              : 'bg-transparent text-zinc-400 hover:text-white hover:bg-zinc-700'
                          }`}
                          variant={hasTips || isRequested ? 'default' : 'ghost'}
                        >
                          <Play className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )
                })}
              </div>
              {/* Pagination */}
              {totalRepertoirePages > 1 && (
                <div className="flex items-center justify-center gap-4 pt-3 mt-3 border-t border-zinc-800">
                  <Button size="sm" variant="outline" onClick={() => setRepertoirePage(p => Math.max(0, p - 1))} disabled={repertoirePage === 0} className="border-zinc-700 h-7 px-3">
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-xs text-zinc-400">{repertoirePage + 1} / {totalRepertoirePages}</span>
                  <Button size="sm" variant="outline" onClick={() => setRepertoirePage(p => Math.min(totalRepertoirePages - 1, p + 1))} disabled={repertoirePage >= totalRepertoirePages - 1} className="border-zinc-700 h-7 px-3">
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-6 text-zinc-400">
              <Music className="h-8 w-8 mx-auto mb-2 text-zinc-600" />
              <p className="text-sm">{t('noSongsFound')}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Replace Song Confirmation */}
      <InlineDialog open={confirmReplaceSong.isOpen} onOpenChange={(open) => !open && setConfirmReplaceSong({ isOpen: false, pendingSong: null })}>
        <InlineDialogHeader onClose={() => setConfirmReplaceSong({ isOpen: false, pendingSong: null })}>
          <InlineDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-400" />
            {t('replaceCurrentSong')}
          </InlineDialogTitle>
          <InlineDialogDescription>
            {t('replaceDescription', { currentSong: nowPlaying?.song.title ?? '', newSong: confirmReplaceSong.pendingSong?.title ?? '' })}
          </InlineDialogDescription>
        </InlineDialogHeader>
        <InlineDialogFooter>
          <Button variant="outline" onClick={() => setConfirmReplaceSong({ isOpen: false, pendingSong: null })} className="bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700">{t('cancel')}</Button>
          <Button onClick={handleConfirmReplace} className="bg-gradient-to-r from-violet-600 to-fuchsia-600">{t('replaceAndPlay')}</Button>
        </InlineDialogFooter>
      </InlineDialog>
    </>
  )
}
